<script setup lang="ts">
import { computed } from "vue";
import { formatCurrency } from "src/utils/currency";
import type { FetchArticleResponse } from "src/api/models/fetchArticleResponse";

interface Props {
  article: FetchArticleResponse;
  disabled?: boolean;
}

const { article } = defineProps<Props>();

const option = computed(() => article.preferred_article_option ?? null);
const supplier = computed(() => option.value?.supplier ?? null);
</script>

<template>
  <div data-testid="article-preferred-option-section">
    <template v-if="option">
      <div data-testid="preferred-option-card" class="border-l-4 border-l-primary surface-card rounded p-3">
        <div class="flex items-center gap-2 mb-3">
          <i class="pi pi-star-fill text-primary text-xs" />
          <span class="font-semibold text-sm">{{ option.brand_name }}</span>

        </div>

        <div class="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div v-if="option.external_reference" class="flex items-center gap-1 text-color-secondary">
            Reference: <span class="font-mono text-color">{{ option.external_reference }}</span>
          </div>
          <div v-if="option.location" class="flex items-center gap-1 text-color-secondary">
            Location: <span class="font-mono text-color">{{ option.location }}</span>
          </div>
        </div>

        <div v-if="supplier" class="mt-3 pt-3 border-t surface-border">

          <div class="text-sm">
            <div v-if="supplier.name" class="flex items-center gap-1 text-color-secondary">
              Supplier: <span class="font-mono text-color">{{ supplier.name }}</span>
            </div>
          </div>
        </div>

        <div class="mt-3 pt-3 border-t surface-border">
          <span class="text-xs font-semibold text-color-secondary uppercase tracking-wide">Pricing</span>
          <div class="flex flex-col gap-1 text-sm mt-2">
            <div class="flex items-center text-color-secondary">
              <span class="min-w-20">Gross:</span>
              <span class="font-mono text-color text-right flex-1">{{ formatCurrency(option.purchase_gross_price ?? 0) }}</span>
            </div>
            <div class="flex items-center text-color-secondary">
              <span class="min-w-20">Discount:</span>
              <span class="font-mono text-color text-right flex-1">{{ option.purchase_discount ?? 0}}%</span>
            </div>
            <div class="flex items-center text-color-secondary border-t surface-border pt-1 mt-1">
              <span class="min-w-20">Net:</span>
              <span class="font-mono text-color text-right flex-1">{{ formatCurrency(option.purchase_net_price ?? 0) }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-else data-testid="preferred-option-empty" class="text-center py-4 text-color-secondary">
      No preferred article option set
    </div>
  </div>
</template>
